import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Truck, Users, Settings, LogOut, UsersRound, Building2, UserCircle, MapPin, X, Car, Webhook, Route, Package } from 'lucide-react';
import { useDeliveryAuth } from '../../auth';
import { useSidebar } from './sidebar-context';
import { useEffect } from 'react';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tasks', label: 'Tasks', icon: ClipboardList },
  { to: '/deliveries', label: 'Deliveries', icon: Truck },
  { to: '/partitions', label: 'Partitions', icon: Package },
  { to: '/optimize', label: 'Optimize', icon: Route },
  { to: '/drivers', label: 'Drivers', icon: Users },
  { to: '/vehicles', label: 'Vehicles', icon: Car },
  { to: '/teams', label: 'Teams', icon: UsersRound },
  { to: '/hubs', label: 'Hubs', icon: Building2 },
  { to: '/recipients', label: 'Recipients', icon: UserCircle },
  { to: '/destinations', label: 'Destinations', icon: MapPin },
  { to: '/webhooks', label: 'Webhooks', icon: Webhook },
];

export function Sidebar() {
  const { logout } = useDeliveryAuth();
  const { isOpen, close } = useSidebar();
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [close]);

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex h-full w-64 flex-col border-r border-border bg-card
          transition-transform duration-300 ease-in-out
          md:static md:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-6">
          <div className="flex items-center">
            <img src="/Flote_Word_Red.png" alt="Flote" className="h-8" />
            <span className="ml-2 text-sm font-medium text-muted-foreground">Delivery</span>
          </div>
          {/* Close button for mobile */}
          <button
            onClick={close}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
            }
          >
            <Settings className="h-5 w-5" />
            Settings
          </NavLink>
          <button
            onClick={logout}
            className="sidebar-link sidebar-link-inactive w-full mt-1"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}
